import { Injectable } from "@angular/core";
import { AlertController } from "@ionic/angular";
import { post, get } from "aws-amplify/api";
import axios, { InternalAxiosRequestConfig, AxiosError } from "axios";
import { AuthService } from "src/app/auth/auth.service";

export interface SalesQuote {
  documentDate?: string;
  customerNumber?: string;
  currencyCode?: string;
  paymentTermsId?: string;
}
@Injectable({
  providedIn: "root",
})
export class BusinessTokenService {
  private env: string;
  private apiUrl: string;
  private token: string | null = null;
  private tokenExpiry: number | null = null;
  private isAdministrator;
  profile: any;

  constructor(auth: AuthService, private alertController: AlertController) {
    this.initialize(auth);
  }

  private async initialize(auth: AuthService) {
    this.isAdministrator = auth.isAdmin();
    this.profile = await auth.getUserProfile();

    this.env = window.location.hostname === "localhost" ? "STAGING2" : "";
    this.apiUrl = `https://api.businesscentral.dynamics.com/v2.0/${this.env}/api/v2.0/`;
    // this.setupAxiosInterceptor();
  }

  private extractErrorMessage(error: any): string {
    return (
      error.response?.data?.message ||
      error.message ||
      "An unknown error occurred"
    );
  }

  async makeGetRequest(path, queryParams = null, env: string) {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

      console.log("Making GET request to:", path, "with params:", queryParams)

    try {
      const response = await post({
        apiName: "businessCentral",
        path: "/getToken", // Adjust this to your Lambda proxy's endpoint
        options: {
          body: {
            method: "GET",
            path, // The API endpoint (e.g., `companies(companyId)/salesQuotes`)
            queryParams, // Optional query parameters
            env: env,
          },
        },
      });
      return JSON.parse(await (await response.response).body.text());
    } catch (error) {
      throw await this.rethrowWithBcDetails("GET", path, error);
    }
  }

  private async showAlert(message: string): Promise<void> {
    // Ensure businessCentralId is set

    const alert = await this.alertController.create({
      header: "Attention",
      message: message,
      buttons: ["OK"],
    });
    await alert.present();
  }

  private checkBusinessCentralId(): void {
    if (
      this.isAdministrator &&
      this.profile?.businessCentralCompany?.businessCentralId.length === 0
    ) {
      const errorMessage =
        "Aucune entreprise n'est sélectionnée. Veuillez en choisir une avant de continuer.";
      console.error(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Make a POST request via the Lambda function
  async makePostRequest(path, data, env: string) {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    try {
      const response = await post({
        apiName: "businessCentral",
        path: "/getToken", // Adjust this to your Lambda proxy's endpoint
        options: {
          body: {
            method: "POST",
            path, // The API endpoint (e.g., `companies(companyId)/salesQuotes`)
            data, // Data to send in the POST request body
            env: env,
          },
        },
      });
      return JSON.parse(await (await response.response).body.text());
    } catch (error) {
      throw await this.rethrowWithBcDetails("POST", path, error);
    }
  }

  /**
   * Amplify v6 REST errors hide the real HTTP payload on `error.response`
   * ({ statusCode, headers, body }); the bare error stringifies to
   * "[object Object]". Extract BC's actual status + body so callers (and the
   * console) get a useful message instead.
   */
  private async rethrowWithBcDetails(
    method: string,
    path: string,
    error: any
  ): Promise<Error> {
    let status: number | undefined;
    let body: any;
    try {
      status = error?.response?.statusCode;
      const rawBody = error?.response?.body;
      body =
        rawBody && typeof rawBody.text === "function"
          ? await rawBody.text()
          : rawBody;
    } catch {
      // ignore — fall back to the original error below
    }

    console.error(`Error making ${method} request:`, {
      path,
      status,
      body,
      name: error?.name,
      message: error?.message,
    });

    if (status || body) {
      const bodyStr = typeof body === "string" ? body : JSON.stringify(body);
      return new Error(
        `BC ${method} ${path} a échoué (${status ?? "?"}) : ${bodyStr}`
      );
    }
    return error;
  }

    // Make a PATCH request via the Lambda function
    async makePatchRequest(path, data, env: string) {
        this.checkBusinessCentralId(); // Ensure businessCentralId is set

        try {
            const response = await post({
            apiName: "businessCentral",
              path: "/getToken", // Adjust this to your Lambda proxy's endpoint
                options: {
                    body: {
                    method: "PATCH",
                    path, // The API endpoint (e.g., `companies(companyId)/salesQuotes`)
                    data, // Data to send in the POST request body
                    env: env,
                    },
                },
            });
            return JSON.parse(await (await response.response).body.text());
        } catch (error) {
            console.error("Error making PATCH request:", error);
            throw error;
        }
    }



  // CRUD Operations for Sales Quotes
  async getSalesQuotesByCompany(companyId: string, env: string): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesQuotes`;
    return this.makeGetRequest(endpoint, null, env);
  }

  async getSalesQuoteById(
    companyId: string,
    salesQuoteId: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesQuotes(${salesQuoteId})`;
    return this.makeGetRequest(endpoint, null, env);
  }

  async createSalesQuote(
    companyId: string,
    salesQuoteData: any,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesQuotes`;
    return this.makePostRequest(endpoint, salesQuoteData, env);
  }

  // Operations for Sales Quote Lines
  async getSalesQuoteLines(
    companyId: string,
    salesQuoteId: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesQuotes(${salesQuoteId})/salesQuoteLines`;
    return this.makeGetRequest(endpoint, null, env);
  }

  async getSalesQuoteLineById(
    companyId: string,
    salesQuoteId: string,
    lineId: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesQuotes(${salesQuoteId})/salesQuoteLines(${lineId})`;
    return this.makeGetRequest(endpoint, null, env);
  }

  async createSalesQuoteLine(
    companyId: string,
    salesQuoteId: string,
    lineData: any,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesQuotes(${salesQuoteId})/salesQuoteLines`;
    return this.makePostRequest(endpoint, lineData, env);
  }

    async getListCustomers(companyId: string, env: string): Promise<any[]> {
        this.checkBusinessCentralId(); // Ensure businessCentralId is set

        const endpoint = `companies(${companyId})/customers?$select=id,number,displayName`;
        let results: any[] = [];
        let nextLink: string | null = endpoint;

        while (nextLink) {
            const response = await this.makeGetRequest(nextLink, null, env);

            if (response && response.value) {
                results.push(...response.value);
            }

            // Check for pagination link
            nextLink = response["@odata.nextLink"] || null;
        }

        return results;
    }

  async getCustomersByName(
    companyId: string,
    customerNumber: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const encodedNumber = encodeURIComponent(customerNumber);
    const endpoint = `companies(${companyId})/customers?$filter=contains(displayName,'${encodedNumber}')`;
    return this.makeGetRequest(endpoint, null, env);
  }
  async getCustomersByNumber(
      companyId: string,
      customerNumber: string,
      env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const encodedNumber = encodeURIComponent(customerNumber);
    const endpoint = `companies(${companyId})/customers?$filter=contains(number,'${encodedNumber}')`;
    return this.makeGetRequest(endpoint, null, env);
  }
  async createInvoice(companyId: string, body: any, env: string): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesInvoices`;
    return this.makePostRequest(endpoint, body, env);
  }
  async createInvoiceLine(
    companyId: string,
    invoiceId: string,
    lineData: any,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesInvoices(${invoiceId})/salesInvoiceLines`;
    return this.makePostRequest(endpoint, lineData, env);
  }

  async createCustomersInBC(
    companyId: string,
    userData: any,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/customers`;
    return this.makePostRequest(endpoint, userData, env);
  }

  async setAgencyCode(
    companyId: string,
    agencyCode: any,
    salesInvoiceLineId: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/salesInvoiceLines(${salesInvoiceLineId})/dimensionSetLines`;
    return this.makePostRequest(endpoint, agencyCode, env);
  }

  async createContact(
    input: any,
    companyId: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/contacts`;
    return this.makePostRequest(endpoint, input, env);
  }

  async updateCustomerInBC(
    companyId: string,
    customerId: string,
    userData: any,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/customers(${customerId})`;
    return this.makePatchRequest(endpoint, userData, env);
  }

  async fetchPaymentTerms(
    companyId: string,
    env: string
  ): Promise<any> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/paymentTerms`;
    return this.makeGetRequest(endpoint, null, env);
  }

  /** Items of the company that can still be invoiced (blocked ones are left out), sorted by number. */
  async getItems(companyId: string, env: string): Promise<BcItem[]> {
    this.checkBusinessCentralId(); // Ensure businessCentralId is set

    const endpoint = `companies(${companyId})/items?$select=number,displayName,blocked`;
    const results: BcItem[] = [];
    let nextLink: string | null = endpoint;

    while (nextLink) {
      const response = await this.makeGetRequest(nextLink, null, env);
      if (response?.value) {
        results.push(...response.value.filter((item: BcItem & { blocked?: boolean }) => !item.blocked));
      }
      nextLink = response?.["@odata.nextLink"] || null;
    }

    return results
      .map(({ number, displayName }) => ({ number, displayName }))
      .sort((a, b) => a.number.localeCompare(b.number));
  }
}

/** A BC item, as offered for an invoice line ("Objet n°"). */
export interface BcItem {
  number: string;
  displayName: string;
}

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
}
